import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/primitives/Button';
import { Box, Fade, Popover, Typography } from '@mui/material';

interface SuggestionItem {
  name: string;
  status?: string;
  diagnosis_id?: string;
  medication_id?: string;
}

interface SuggestionPopoverProps {
  suggestions: SuggestionItem[];
  type: 'diagnosis' | 'medication';
  patientId?: string;
  count: number;
  variant?: 'badge' | 'dot' | 'text';
}

export const SuggestionPopover: React.FC<SuggestionPopoverProps> = ({
  suggestions,
  type,
  patientId,
  count,
  variant = 'badge',
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const closeTimeoutRef = useRef<NodeJS.Timeout>();
  const popoverRef = useRef<HTMLDivElement>(null);

  // Filter only suggested items
  const suggestedItems = suggestions.filter(item => item.status === 'suggested');
  const displayCount = count || suggestedItems.length;

  // Don't render if no suggestions
  if (displayCount === 0) return null;

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    clearTimeout(closeTimeoutRef.current);
    const target = event.currentTarget;
    hoverTimeoutRef.current = setTimeout(() => {
      setAnchorEl(target);
    }, 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    // Delay close to allow mouse to move to popover
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  const handlePopoverEnter = () => {
    clearTimeout(closeTimeoutRef.current);
  };

  const handlePopoverLeave = () => {
    setAnchorEl(null);
  };

  const handleManageClick = () => {
    if (patientId) {
      navigate(`/patient/${patientId}`);
    }
    setAnchorEl(null);
  };

  const typeLabel = type === 'diagnosis' ? 'Diagnosis' : 'Medication';
  const typeLabelPlural = type === 'diagnosis' ? 'Diagnoses' : 'Medications';

  // Define styles based on variant
  const getBadgeStyles = () => {
    const baseStyles = {
      cursor: 'pointer',
      ml: 0.75,
      transition: 'all 0.2s ease',
    };

    switch (variant) {
      case 'dot':
        return {
          ...baseStyles,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          '& .dot': {
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: 'info.main',
            opacity: 0.8,
          },
          '& .count': {
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'info.main',
          },
          '&:hover': {
            '& .dot': {
              opacity: 1,
              transform: 'scale(1.3)',
            },
            '& .count': {
              textDecoration: 'underline',
            },
          },
        };

      case 'text':
        return {
          ...baseStyles,
          display: 'inline',
          color: 'text.secondary',
          fontSize: '0.75rem',
          opacity: 0.7,
          ml: 0.5,
          '&:hover': {
            opacity: 1,
            textDecoration: 'underline',
          },
        };

      case 'badge':
      default:
        return {
          ...baseStyles,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 0.5,
          py: 0.125,
          borderRadius: '10px',
          backgroundColor: 'rgba(33, 150, 243, 0.12)',
          color: 'info.main',
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1.2,
          minWidth: '18px',
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.18)',
            transform: 'scale(1.05)',
          },
          animation: 'subtleEntrance 0.3s ease-out',
          '@keyframes subtleEntrance': {
            '0%': {
              opacity: 0,
              transform: 'scale(0.9)',
            },
            '100%': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        };
    }
  };

  return (
    <>
      {/* Badge trigger */}
      <Box
        component="span"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          if (!anchorEl) {
            setAnchorEl(e.currentTarget);
          } else {
            setAnchorEl(null);
          }
        }}
        sx={getBadgeStyles()}
      >
        {variant === 'dot' ? (
          <>
            <Box className="dot" />
            <Box className="count">{displayCount}</Box>
          </>
        ) : variant === 'text' ? (
          `(${displayCount})`
        ) : (
          displayCount
        )}
      </Box>

      {/* Popover content */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        TransitionComponent={Fade}
        transitionDuration={200}
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': {
            pointerEvents: 'auto',
            maxWidth: '90vw', // Prevent overflow on small screens
            mt: 0.5,
            borderRadius: 2, // 16px - matches button border radius
            border: '2px solid',
            borderColor: 'borderColors.primary',
          },
        }}
      >
        <Box
          ref={popoverRef}
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
          sx={{ p: 2 }}
        >
          {/* Header */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {displayCount} Pending {typeLabel} Suggestion{displayCount > 1 ? 's' : ''}
          </Typography>

          {/* Suggestions list */}
          {suggestedItems.length > 0 && (
            <Box component="ul" sx={{ margin: 0, pl: 2.5, py: 0, mb: 1.5 }}>
              {suggestedItems.slice(0, suggestedItems.length <= 5 ? 5 : 4).map((item, index) => (
                <Typography
                  component="li"
                  key={item.diagnosis_id || item.medication_id || index}
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.875rem',
                    listStyleType: 'disc',
                    mb: 0.5,
                  }}
                >
                  {item.name}
                </Typography>
              ))}
              {suggestedItems.length > 5 && (
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.875rem',
                    listStyleType: 'none',
                    fontStyle: 'italic',
                    ml: -1.5,
                  }}
                >
                  ...and {suggestedItems.length - 4} more
                </Typography>
              )}
            </Box>
          )}

          {/* Action button */}
          {patientId && (
            <Button size="small" variant="contained" onClick={handleManageClick}>
              Manage {typeLabelPlural}
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
};
